import React from "react";
import { TopNav } from "@/components/TopNav";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Flame, Droplet, Sparkles, Zap, Radiation, CheckCircle2, Search, ShieldCheck, Scale, FileCheck2, Lock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pageTr } from "@/i18n/pageTranslations";

const DATA = {
  sapphire: { color: "#6B9EE8", accent: "rgba(15,82,186,0.22)", img: "/gems/sapphire.png",
    titleFr: "SAPHIRS", titleEn: "SAPPHIRES",
    lead: { fr: "Les saphirs naturels peuvent être traités pour améliorer leur couleur, leur clarté ou leur apparence.", en: "Natural sapphires can be treated to improve their color, clarity or appearance." },
    treats: [
      { icon: Flame, name: "Chauffe (Heating)", fr: "Traitement le plus courant. Améliore la couleur et la clarté en réduisant les inclusions et en intensifiant la couleur.", en: "Most common. Improves color and clarity by reducing inclusions and intensifying color." },
      { icon: Droplet, name: "Remplissage par verre", fr: "Le verre comble les fractures et cavités pour améliorer la clarté. Moins stable que la chauffe. Non accepté par le marché haut de gamme.", en: "Glass fills fractures and cavities to improve clarity. Less stable than heating. Not accepted in high-end market." },
      { icon: Sparkles, name: "Diffusion", fr: "Apport d'éléments chimiques en surface pour modifier ou intensifier la couleur. Couche superficielle uniquement.", en: "Surface diffusion of chemicals to modify or intensify color. Surface layer only." },
      { icon: Zap, name: "Traitements spéciaux", fr: "Béryllium (Be) pour obtenir des saphirs jaunes, diffusion + chauffe combinées, etc.", en: "Beryllium (Be) for yellow sapphires, combined diffusion + heating, etc." },
    ],
    untreated: { fr: "Non traité : rare et très recherché. Les saphirs non traités sont plus rares et plus précieux. Toujours demander un certificat gemmologique.", en: "Untreated: rare and highly sought. Untreated sapphires are rarer and more valuable. Always request a gemmological certificate." },
  },
  ruby: { color: "#E86B8C", accent: "rgba(224,17,95,0.22)", img: "/gems/ruby.png",
    titleFr: "RUBIS", titleEn: "RUBIES",
    lead: { fr: "Les rubis naturels sont souvent traités pour améliorer leur couleur et leur clarté.", en: "Natural rubies are often treated to improve color and clarity." },
    treats: [
      { icon: Flame, name: "Chauffe (Heating)", fr: "Très courant. Améliore la couleur (rouge plus vif) et la clarté en réduisant les inclusions. Accepté par le marché.", en: "Very common. Improves color (brighter red) and clarity by reducing inclusions. Accepted by market." },
      { icon: Droplet, name: "Remplissage par verre (Glass filling)", fr: "Remplit les fractures pour améliorer l'aspect. Peut rendre la pierre moins durable. Non accepté par le marché haut de gamme.", en: "Fills fractures to improve appearance. Can make the stone less durable. Not accepted in high-end market." },
      { icon: Sparkles, name: "Diffusion", fr: "Apporte du chrome en surface pour intensifier le rouge. Couche superficielle, peut s'estomper à l'usure.", en: "Adds chromium at surface to intensify red. Surface layer, may fade with wear." },
      { icon: Radiation, name: "Irradiation + Chauffe", fr: "Utilisé pour modifier ou intensifier la couleur, notamment sur rubis pâles ou rosés.", en: "Used to modify or intensify color, especially on pale or pinkish rubies." },
    ],
    untreated: { fr: "Non traité : extrêmement rare. Les rubis non traités, notamment 'sang de pigeon' de Birmanie, sont parmi les plus recherchés. Toujours demander un certificat gemmologique.", en: "Untreated: extremely rare. Untreated rubies, especially Burmese 'pigeon blood', are among the most sought-after. Always request a gemmological certificate." },
  },
  emerald: { color: "#6BE895", accent: "rgba(80,200,120,0.22)", img: "/gems/emerald.png",
    titleFr: "ÉMERAUDES", titleEn: "EMERALDS",
    lead: { fr: "Les émeraudes contiennent presque toujours des inclusions naturelles. Les traitements visent à améliorer leur clarté et leur stabilité.", en: "Emeralds almost always contain natural inclusions. Treatments aim to improve clarity and stability." },
    treats: [
      { icon: Droplet, name: "Huilage (Oiling)", fr: "Le traitement le plus courant. Des huiles végétales incolores pénètrent dans les fissures pour améliorer la clarté. Nécessite parfois un renouvellement.", en: "Most common treatment. Colorless vegetable oils penetrate fissures to improve clarity. Sometimes needs renewal." },
      { icon: Droplet, name: "Résine / Polymère", fr: "Matériaux plus stables que l'huile. Améliorent la clarté et renforcent la pierre. Accepté sur le marché.", en: "More stable materials than oil. Improve clarity and reinforce the stone. Accepted on the market." },
      { icon: Sparkles, name: "Imprégnation", fr: "Résines ou polymères sous vide pour combler les fissures et augmenter la clarté.", en: "Resins or polymers under vacuum to fill fissures and increase clarity." },
      { icon: Zap, name: "Traitements spéciaux", fr: "Stabilisation, purification, amélioration de la couleur (rares et spécifiques selon l'origine).", en: "Stabilization, purification, color enhancement (rare and specific per origin)." },
    ],
    untreated: { fr: "Non traité : très rare. Les émeraudes non huilées sont extrêmement rares et peuvent montrer plus d'inclusions visibles. Toujours demander un certificat gemmologique.", en: "Untreated: very rare. Non-oiled emeralds are extremely rare and may show more visible inclusions. Always request a certificate." },
  },
};

const TreatmentsPage = () => {
  const { lang } = useLang();
  const t = pageTr[lang].treatments;
  const REM_ICONS = [Search, ShieldCheck, Scale, FileCheck2];
  return (
    <div className="min-h-screen bg-[#050811] text-[#F8F5EE] flex flex-col">
      <SEO
        title={t.title}
        path="/treatments"
        noindex
        description={lang === "fr"
          ? "GEM PRICE — Traitements des pierres précieuses (chauffe, huilage, remplissage) et leur impact sur la valeur. Référence pour évaluer saphirs, rubis, émeraudes."
          : "GEM PRICE — Treatments of precious gemstones (heat, oiling, filling) and their impact on value. Reference to evaluate sapphires, rubies, emeralds."}
      />
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 md:px-14 py-10 space-y-8">
        <header className="text-center">
          <h1 className="font-serif text-3xl md:text-5xl font-light tracking-wide text-[#E2CFA4]">{t.title.toUpperCase()}</h1>
          <p className="mt-2 text-[13px] text-[#A1A7B3]">{t.subtitle}</p>
        </header>

        <div className="grid lg:grid-cols-3 gap-6">
          {Object.entries(DATA).map(([id, g]) => (
            <article key={id} data-testid={`treatment-${id}`} className="rounded-sm border border-[#C1A661]/15 bg-[#080C17] p-6"
              style={{ boxShadow: `inset 0 0 80px ${g.accent}` }}>
              <div className="flex items-center gap-4 mb-4">
                <img src={g.img} alt={id} className="w-20 h-20 object-cover rounded-sm" />
                <div>
                  <h2 className="font-serif text-2xl" style={{ color: g.color }}>{lang === "fr" ? g.titleFr : g.titleEn}</h2>
                  <p className="text-[11px] text-[#A1A7B3] font-light mt-1">{g.lead[lang]}</p>
                </div>
              </div>
              <div className="text-center text-[10px] tracking-[0.3em] uppercase py-2 border-y border-[#C1A661]/15 mb-4" style={{ color: g.color }}>{t.main}</div>
              <div className="space-y-4">
                {g.treats.map((tr) => {
                  const Icon = tr.icon;
                  return (
                    <div key={tr.name} className="flex gap-3">
                      <Icon size={18} strokeWidth={1.4} style={{ color: g.color }} className="shrink-0 mt-0.5" />
                      <div>
                        <div className="text-[11px] tracking-[0.15em] uppercase font-medium" style={{ color: g.color }}>{tr.name}</div>
                        <p className="text-[12px] text-[#D8DBE2] font-light mt-1 leading-relaxed">{tr[lang]}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-[#C1A661]/15 flex gap-2">
                <CheckCircle2 size={14} className="shrink-0 mt-0.5" style={{ color: g.color }} />
                <p className="text-[11px] text-[#D8DBE2] font-light leading-relaxed">{g.untreated[lang]}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="border border-[#C1A661]/20 rounded-sm p-6 bg-[#080C17]">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C1A661] md:col-span-1">{t.remember}</div>
            {t.rememberItems.map((r, i) => {
              const Icon = REM_ICONS[i];
              return (
                <div key={i} className="flex gap-2">
                  <Icon size={16} className="text-[#C1A661] shrink-0 mt-0.5" strokeWidth={1.3} />
                  <p className="text-[11px] text-[#A1A7B3] font-light">{r}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-[#C1A661]/40 rounded-sm p-5 flex items-center gap-4 bg-[#080C17]/60">
          <Lock size={16} className="text-[#C1A661] shrink-0" />
          <p className="text-[11px] tracking-[0.18em] uppercase text-[#E2CFA4] text-center flex-1">{t.subscribeCta}</p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TreatmentsPage;
